import React, { useEffect, useMemo, useState } from "react";
import { api, fmtDate, formatErr } from "../../api";
import { SectionTitle, EmptyState } from "../../components/Atoms";
import { Envelope, Phone, Buildings, Trash, ChatCircleText, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_META = {
  new:       { label: "New",       color: "#22D3EE" },
  contacted: { label: "Contacted", color: "#F59E0B" },
  qualified: { label: "Qualified", color: "#A3E635" },
  won:       { label: "Won",       color: "#10B981" },
  lost:      { label: "Lost",      color: "#EC4899" },
};
const STATUS_ORDER = ["new", "contacted", "qualified", "won", "lost"];

export default function AdminLeadsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const url = statusFilter ? `/leads?status=${statusFilter}` : "/leads";
      const { data } = await api.get(url);
      setRows(data || []);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.email || "").toLowerCase().includes(q) ||
      (r.company || "").toLowerCase().includes(q)
    );
  }, [rows, filter]);

  const counts = useMemo(() => {
    const c = { all: rows.length };
    STATUS_ORDER.forEach(s => { c[s] = rows.filter(r => r.status === s).length; });
    return c;
  }, [rows]);

  const updateStatus = async (id, next) => {
    try {
      await api.patch(`/leads/${id}`, { status: next });
      toast.success(`Marked as ${STATUS_META[next]?.label || next}`);
      await load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted");
      setDetail(null);
      await load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const saveNotes = async (id, notes) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, { notes });
      toast.success("Notes saved");
      setDetail(data);
      await load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-leads-page">
      <SectionTitle
        title="Sales Leads"
        subtitle="Everyone who submitted the Talk to Sales / Book Demo form on the marketing site."
      />

      <div className="flex flex-wrap items-center gap-2">
        <input className="input-base max-w-sm" placeholder="Search by name, email or company"
               value={filter} onChange={e => setFilter(e.target.value)}
               data-testid="lead-search" />
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusChip active={!statusFilter} count={counts.all} onClick={() => setStatusFilter("")} label="All" color="#94A3B8" testid="lead-filter-all" />
          {STATUS_ORDER.map(s => (
            <StatusChip key={s}
              active={statusFilter === s} count={counts[s] || 0}
              onClick={() => setStatusFilter(s)}
              label={STATUS_META[s].label} color={STATUS_META[s].color}
              testid={`lead-filter-${s}`} />
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-muted text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState title="No leads yet" subtitle="Submissions from the marketing site will appear here." icon={Envelope} />
      ) : (
        <div className="grid gap-2">
          {filtered.map(l => {
            const meta = STATUS_META[l.status] || STATUS_META.new;
            return (
              <div key={l.id} className="glass-soft p-4 flex flex-wrap items-center gap-3 hover-lift"
                   data-testid={`lead-row-${l.id}`}>
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#22D3EE] to-[#8B5CF6] grid place-items-center text-white font-black">
                  {(l.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold truncate">{l.name}
                    {l.company && <span className="ml-2 text-[11px] text-muted font-normal">· {l.company}</span>}
                  </div>
                  <div className="text-[11px] text-muted mt-0.5 flex flex-wrap items-center gap-3">
                    <span className="flex items-center gap-1"><Envelope size={11} weight="bold" /> {l.email}</span>
                    {l.phone && <span className="flex items-center gap-1"><Phone size={11} weight="bold" /> {l.phone}</span>}
                    <span className="pill text-[10px]" style={{color: meta.color, background: `${meta.color}22`, borderColor: `${meta.color}55`}}>
                      {meta.label}
                    </span>
                    <span>{fmtDate(l.created_at)}</span>
                    {l.source && <span className="text-[10px] text-muted opacity-70">via {l.source}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <select value={l.status} onChange={e => updateStatus(l.id, e.target.value)}
                          className="input-base text-xs py-1.5 pr-2"
                          data-testid={`lead-status-${l.id}`}>
                    {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                  </select>
                  <button onClick={() => setDetail(l)} className="btn-ghost text-xs flex items-center gap-1"
                          data-testid={`lead-view-${l.id}`}>
                    <ChatCircleText size={12} weight="bold" /> View
                  </button>
                  <button onClick={() => remove(l.id)} className="btn-ghost text-xs flex items-center gap-1 text-[#EC4899]"
                          data-testid={`lead-delete-${l.id}`}>
                    <Trash size={12} weight="bold" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {detail && <LeadDetail lead={detail} onClose={() => setDetail(null)} onSave={saveNotes} onDelete={remove} />}
    </div>
  );
}

function StatusChip({ active, count, onClick, label, color, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`pill text-[11px] flex items-center gap-1.5 ${active ? "ring-1" : "opacity-70 hover:opacity-100"}`}
      style={{ color, background: `${color}1a`, borderColor: `${color}55` }}>
      {label} <span className="text-[10px] opacity-80">{count}</span>
    </button>
  );
}

function LeadDetail({ lead, onClose, onSave, onDelete }) {
  const [notes, setNotes] = useState(lead.notes || "");
  const meta = STATUS_META[lead.status] || STATUS_META.new;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="glass w-full max-w-lg p-5 relative" onClick={e => e.stopPropagation()} data-testid="lead-detail">
        <button onClick={onClose} className="absolute top-3 right-3 text-muted hover:text-white"><X size={18} /></button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#22D3EE] to-[#8B5CF6] grid place-items-center text-white font-black">
            {(lead.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-black">{lead.name}</div>
            <div className="text-[11px] text-muted">{lead.company || "—"}</div>
          </div>
          <span className="pill text-[10px]" style={{color: meta.color, background: `${meta.color}22`, borderColor: `${meta.color}55`}}>
            {meta.label}
          </span>
        </div>

        <div className="mt-4 space-y-2 text-sm">
          <Row icon={Envelope} label="Email"><a href={`mailto:${lead.email}`} className="text-[#22D3EE]" data-testid="lead-detail-email">{lead.email}</a></Row>
          {lead.phone && <Row icon={Phone} label="Phone"><a href={`tel:${lead.phone}`} className="text-white">{lead.phone}</a></Row>}
          {lead.company && <Row icon={Buildings} label="Company">{lead.company}</Row>}
          <Row icon={ChatCircleText} label="Source">{lead.source} · {fmtDate(lead.created_at)}</Row>
        </div>

        {lead.message && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-muted mb-1.5">Their message</div>
            <div className="glass-soft p-3 text-sm text-white whitespace-pre-wrap">{lead.message}</div>
          </div>
        )}

        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted mb-1.5">Internal notes</div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                    className="input-base" placeholder="Called on Wed. Follow-up next Mon…"
                    data-testid="lead-notes" />
        </div>

        <div className="mt-5 flex justify-between">
          <button onClick={() => onDelete(lead.id)} className="btn-ghost text-xs flex items-center gap-1 text-[#EC4899]"
                  data-testid="lead-detail-delete">
            <Trash size={12} weight="bold" /> Delete lead
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-ghost text-sm">Close</button>
            <button onClick={() => onSave(lead.id, notes)} className="btn-primary text-sm" data-testid="lead-detail-save">Save notes</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex items-center gap-3 text-white">
    <Icon size={14} weight="bold" className="text-muted" />
    <div className="text-[11px] uppercase tracking-wider text-muted font-bold min-w-[70px]">{label}</div>
    <div className="flex-1 min-w-0 truncate">{children}</div>
  </div>
);
